/**
 * CampusInputView — the campus General Manager's input space.
 *
 * A campus GM (8 Sep 2026): "you've got an HCC that's incredibly strong, but that
 * person might be a better fit in student experience"; the GM sees the campus
 * daily and answers for succession on it, yet most of the campus reports up a
 * function, not to them. So this is the whole campus, ungated: no seat, no
 * window, no waiting for a People Leader to submit. Input given before a
 * session shapes the room; given after, it is the campus's verification.
 *
 * What it does NOT show is the 9-box. The block, the impact category and the
 * potential are the review's own judgment and stay with the People Leader and
 * the room; the published high-potential and key-person designations do show,
 * because those are what the GM is asked to contribute to.
 */
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Building2, Check, Loader2, MessageSquarePlus, Plus, Search, Shuffle, Star, UserCheck, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useCampusOptions, useCampusView, useSaveCampusInput } from '@/hooks/useTalentReview'
import type { CampusInput, CampusPerson } from '@/services/api/talentReviewService'
import RoleTitlePicker from './RoleTitlePicker'
import { READINESS_LABEL, READINESS_ORDER, type Readiness } from './eduvosBlocks'

/** A role in the draft, before it is saved. Blank rows are dropped on save. */
interface RoleDraft {
  title: string
  readiness: Readiness | ''
}

/** Three is the cap the growth plan uses for the same question. */
const MAX_ROLES = 3

interface Draft {
  insight: string
  growthAreas: string
  keyPerson: boolean
  keyPersonReason: string
  roles: RoleDraft[]
  crossArea: string
  crossNote: string
}

const emptyRole: RoleDraft = { title: '', readiness: '' }

const emptyDraft: Draft = {
  insight: '',
  growthAreas: '',
  keyPerson: false,
  keyPersonReason: '',
  roles: [{ ...emptyRole }],
  crossArea: '',
  crossNote: '',
}

const draftOf = (i: CampusInput | null): Draft =>
  i
    ? {
        insight: i.insight || '',
        growthAreas: i.growth_areas || '',
        keyPerson: i.key_person,
        keyPersonReason: i.key_person_reason || '',
        roles: (i.succeeding_roles || []).length
          ? (i.succeeding_roles || []).map((r) => ({ title: r.title, readiness: (r.readiness as Readiness) || '' }))
          : [{ ...emptyRole }],
        crossArea: i.cross_functional_area || '',
        crossNote: i.cross_functional_note || '',
      }
    : { ...emptyDraft, roles: [{ ...emptyRole }] }

const namedRoles = (d: Draft) => d.roles.filter((r) => r.title.trim())

const hasSomething = (d: Draft) =>
  !!(d.insight.trim() || d.growthAreas.trim() || d.keyPerson || namedRoles(d).length || d.crossArea.trim() || d.crossNote.trim())

/** One author's input, in the words the room reads. */
export function CampusInputLines({ input, className }: { input: CampusInput; className?: string }) {
  return (
    <div className={cn('space-y-1 text-xs', className)}>
      <p className="font-medium text-foreground">
        {input.author_name || 'A campus General Manager'}
        {input.author_job_title ? <span className="font-normal text-muted-foreground"> · {input.author_job_title}</span> : null}
      </p>
      {input.insight && (
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Key strengths:</span> {input.insight}
        </p>
      )}
      {input.growth_areas && (
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Key growth areas:</span> {input.growth_areas}
        </p>
      )}
      {input.key_person && (
        <p className="text-amber-800">
          <span className="font-medium">Key person:</span> {input.key_person_reason}
        </p>
      )}
      {(input.succeeding_roles || []).map((r) => (
        <p className="text-indigo-800" key={r.title}>
          <span className="font-medium">Successor for {r.title}</span>
          {r.readiness ? ` · ${READINESS_LABEL[r.readiness as Readiness] || r.readiness}` : ''}
        </p>
      ))}
      {(input.cross_functional_area || input.cross_functional_note) && (
        <p className="text-emerald-800">
          <span className="font-medium">Would also fit{input.cross_functional_area ? ` in ${input.cross_functional_area}` : ' elsewhere'}:</span>{' '}
          {input.cross_functional_note}
        </p>
      )}
    </div>
  )
}

export default function CampusInputView({ reviewYear, className }: { reviewYear: number; className?: string }) {
  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsFailed,
    refetch: refetchOptions,
  } = useCampusOptions(reviewYear)
  const campuses = options?.campuses || []
  const [campus, setCampus] = useState<string | null>(null)
  const chosen = campus || campuses[0]?.name || null
  const { data, isLoading, isError: viewFailed, refetch: refetchView } = useCampusView(reviewYear, chosen)
  const { mutate: save, isPending: saving } = useSaveCampusInput()

  const [q, setQ] = useState('')
  const [department, setDepartment] = useState('all')
  const [leader, setLeader] = useState('all')
  const [only, setOnly] = useState<'all' | 'mine' | 'none'>('all')
  const [editing, setEditing] = useState<CampusPerson | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft)

  const people = data?.people || []
  // Departments only. Offering the Role Master function beside them put
  // Facilities and Finance in one list as though they were the same kind of
  // thing, which is too broad to act on (HR process owner, 9 Sep 2026).
  const areas = useMemo(
    () => Array.from(new Set(data?.facets.departments || [])).sort((a, b) => a.localeCompare(b)),
    [data],
  )
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return people.filter((p) => {
      if (needle && ![p.name, p.job_title, p.department, p.function, p.team, p.leader_name].some((v) => (v || '').toLowerCase().includes(needle)))
        return false
      if (department !== 'all' && p.department !== department) return false
      if (leader !== 'all' && p.leader_id !== leader) return false
      if (only === 'mine' && !p.my_input) return false
      if (only === 'none' && p.inputs.length > 0) return false
      return true
    })
  }, [people, q, department, leader, only])

  const open = (p: CampusPerson) => {
    setEditing(p)
    setDraft(draftOf(p.my_input))
  }

  const submit = () => {
    if (!editing) return
    if (draft.keyPerson && !draft.keyPersonReason.trim()) {
      toast.error('Say why they are a key person.')
      return
    }
    const roles = namedRoles(draft)
    const noReadiness = roles.find((r) => !r.readiness)
    if (noReadiness) {
      toast.error(`${noReadiness.title.trim()} needs a readiness.`)
      return
    }
    const titles = roles.map((r) => r.title.trim().toLowerCase())
    if (new Set(titles).size !== titles.length) {
      toast.error('That role is named twice.')
      return
    }
    const clearing = !hasSomething(draft)
    save(
      {
        employeeId: editing.employee_id,
        body: {
          review_year: reviewYear,
          insight: draft.insight.trim() || null,
          growth_areas: draft.growthAreas.trim() || null,
          key_person: draft.keyPerson,
          key_person_reason: draft.keyPerson ? draft.keyPersonReason.trim() : null,
          succeeding_roles: namedRoles(draft).map((r) => ({
            title: r.title.trim(),
            readiness: (r.readiness || null) as Readiness | null,
          })),
          cross_functional_area: draft.crossArea.trim() || null,
          cross_functional_note: draft.crossNote.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(clearing ? `Your input on ${editing.name} was removed.` : `Your input on ${editing.name} is saved.`)
          setEditing(null)
        },
        onError: (err) => toast.error(err.message || 'Could not save your input'),
      },
    )
  }

  if (optionsLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Finding your campuses…
        </CardContent>
      </Card>
    )
  }
  if (optionsFailed) {
    return (
      <Card className={className}>
        <CardContent className="space-y-3 py-10 text-center">
          <p className="text-sm font-medium">The campus list did not answer.</p>
          <p className="text-xs text-muted-foreground">
            The staff directory was not reachable just then. Nothing you have written is affected.
          </p>
          <Button size="sm" variant="outline" onClick={() => void refetchOptions()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    )
  }
  if (campuses.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="pt-6 text-center text-sm text-muted-foreground">
          The campus input space is for whoever answers for a campus. No campus is assigned to you for this year — an
          administrator assigns one under Manage user roles.
        </CardContent>
      </Card>
    )
  }

  const s = data?.summary
  const tiles = [
    { key: 'total', label: 'People on the campus', value: s?.total ?? 0, sub: 'Every reporting line' },
    { key: 'input', label: 'You have written about', value: s?.with_input ?? 0, sub: 'Insight, key person, successor or fit' },
    { key: 'kp', label: 'Key persons named', value: s?.key_persons_nominated ?? 0, sub: 'By the campus' },
    { key: 'succ', label: 'Successors named', value: s?.successors_nominated ?? 0, sub: 'For a role on the campus' },
    { key: 'cross', label: 'Cross-functional fits', value: s?.cross_functional ?? 0, sub: 'Would do well elsewhere' },
  ]

  return (
    <div className={cn('space-y-4', className)} data-demo="talent-campus-view">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-heritage-blue" />
              <CardTitle className="text-base">Your campus: what you see in the people</CardTitle>
            </div>
            {campuses.length > 1 && (
              <Select value={chosen ?? ''} onValueChange={(v) => setCampus(v)}>
                <SelectTrigger className="h-8 w-[20rem] text-xs" aria-label="Campus" data-demo="talent-campus-picker">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {campuses.map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.label || c.name} · {c.people} {c.people === 1 ? 'person' : 'people'}
                      {c.inputs ? ` · ${c.inputs} written` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <CardDescription>
            Everyone on the campus, whatever their reporting line, and whether or not their People Leader has submitted. Add what you see:
            an observation, a key person to keep, a successor for a role, or someone who would do better in another part of the campus.
            The Annual I&G Review reads it on the person, whenever you write it — before a session it shapes the discussion, after one it
            stands as the campus's own view. Placements stay with the People Leader and the room.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {tiles.map((t) => (
              <div key={t.key} className="rounded-lg border border-l-4 border-l-heritage-blue/60 p-2.5">
                <p className="text-xl font-bold">{t.value}</p>
                <p className="text-xs font-medium">{t.label}</p>
                <p className="text-[11px] text-muted-foreground">{t.sub}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[14rem] flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search a name, role, team or People Leader"
                className="h-8 pl-8 text-sm"
                aria-label="Search the campus"
              />
            </div>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="h-8 w-[12rem] text-xs" aria-label="Department">
                <SelectValue placeholder="Every department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every department</SelectItem>
                {(data?.facets.departments || []).map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={leader} onValueChange={setLeader}>
              <SelectTrigger className="h-8 w-[13rem] text-xs" aria-label="People Leader">
                <SelectValue placeholder="Every People Leader" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every People Leader</SelectItem>
                {(data?.facets.leaders || []).map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={only} onValueChange={(v) => setOnly(v as typeof only)}>
              <SelectTrigger className="h-8 w-[13rem] text-xs" aria-label="Input given">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Everyone</SelectItem>
                <SelectItem value="mine">Only those you wrote about</SelectItem>
                <SelectItem value="none">Only those with nothing yet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="flex items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading the campus…
            </p>
          ) : viewFailed ? (
            <div className="space-y-3 p-6 text-center">
              <p className="text-sm font-medium">The campus did not load.</p>
              <p className="text-xs text-muted-foreground">Nothing you have written is affected.</p>
              <Button size="sm" variant="outline" onClick={() => void refetchView()}>
                Try again
              </Button>
            </div>
          ) : shown.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Nobody matches those filters.</p>
          ) : (
            <ul className="divide-y">
              {shown.map((p) => (
                <li key={p.employee_id} className="flex flex-wrap items-start justify-between gap-3 p-3" data-demo="talent-campus-row">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {p.name}
                      {p.job_title ? <span className="font-normal text-muted-foreground"> · {p.job_title}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[p.department, p.team, p.leader_name ? `reports to ${p.leader_name}` : null, p.employment_category]
                        .filter(Boolean)
                        .join(' · ')}
                      {p.tenure_years != null ? ` · ${p.tenure_years} years at Eduvos` : ''}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {p.is_hipo && (
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
                          <Star className="mr-1 h-3 w-3" /> High-potential
                        </Badge>
                      )}
                      {p.key_person && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                          Key person
                        </Badge>
                      )}
                      {p.my_input?.key_person && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                          <UserCheck className="mr-1 h-3 w-3" /> You named them a key person
                        </Badge>
                      )}
                      {(p.my_input?.succeeding_roles || []).map((r) => (
                        <Badge key={r.title} variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-800">
                          Successor: {r.title}
                        </Badge>
                      ))}
                      {(p.my_input?.cross_functional_area || p.my_input?.cross_functional_note) && (
                        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
                          <Shuffle className="mr-1 h-3 w-3" /> Cross-functional fit
                        </Badge>
                      )}
                    </div>
                    {p.inputs.filter((i) => i.author_id !== p.my_input?.author_id).length > 0 && (
                      <details className="mt-1.5">
                        <summary className="cursor-pointer list-none text-[11px] text-muted-foreground">
                          {p.inputs.length - (p.my_input ? 1 : 0)} other input on this person
                        </summary>
                        <div className="mt-1 space-y-2 rounded-md border bg-muted/30 p-2">
                          {p.inputs
                            .filter((i) => i.author_id !== p.my_input?.author_id)
                            .map((i) => (
                              <CampusInputLines key={i.id} input={i} />
                            ))}
                        </div>
                      </details>
                    )}
                    {p.my_input?.insight && <p className="mt-1 text-xs text-muted-foreground">“{p.my_input.insight}”</p>}
                  </div>
                  <Button
                    size="sm"
                    variant={p.my_input ? 'outline' : 'default'}
                    className="h-8 shrink-0 text-xs"
                    onClick={() => open(p)}
                    data-demo="talent-campus-input-open"
                  >
                    {p.my_input ? (
                      <>
                        <Check className="mr-1 h-3.5 w-3.5" /> Your input
                      </>
                    ) : (
                      <>
                        <MessageSquarePlus className="mr-1 h-3.5 w-3.5" /> Add your input
                      </>
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="w-[95vw] sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editing?.name}</DialogTitle>
            <DialogDescription>
              {[editing?.job_title, editing?.department, editing?.leader_name ? `reports to ${editing.leader_name}` : null]
                .filter(Boolean)
                .join(' · ')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium" htmlFor="campus-insight">
                Key strengths
              </label>
              <Textarea
                id="campus-insight"
                value={draft.insight}
                onChange={(e) => setDraft((d) => ({ ...d, insight: e.target.value }))}
                rows={3}
                className="mt-1 text-sm"
                placeholder="What they actually do on the ground, and how they do it"
              />
            </div>

            <div>
              <label className="text-sm font-medium" htmlFor="campus-growth">
                Key growth areas
              </label>
              <Textarea
                id="campus-growth"
                value={draft.growthAreas}
                onChange={(e) => setDraft((d) => ({ ...d, growthAreas: e.target.value }))}
                rows={3}
                className="mt-1 text-sm"
                placeholder="Where they would have to grow to go further"
              />
            </div>

            <div className="rounded-lg border p-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={draft.keyPerson} onCheckedChange={(v) => setDraft((d) => ({ ...d, keyPerson: v === true }))} />
                <span className="font-medium">A key person for the campus</span>
              </label>
              {draft.keyPerson && (
                <Textarea
                  value={draft.keyPersonReason}
                  onChange={(e) => setDraft((d) => ({ ...d, keyPersonReason: e.target.value }))}
                  rows={2}
                  className="mt-2 text-sm"
                  placeholder="Why is this person a key person?"
                  aria-label="Why is this person a key person?"
                />
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-2" data-demo="talent-campus-roles">
              <p className="text-sm font-medium">You see them succeeding into a role</p>
              <p className="text-xs text-muted-foreground">
                More than one path is fine — a campus ManCo role rarely has only one.
              </p>
              {draft.roles.map((r, i) => (
                <div className="flex flex-wrap items-center gap-2" key={i}>
                  <RoleTitlePicker
                    value={r.title}
                    onChange={(title) =>
                      setDraft((d) => ({ ...d, roles: d.roles.map((x, j) => (j === i ? { ...x, title } : x)) }))
                    }
                    className="min-w-[15rem] flex-1"
                    placeholder={i === 0 ? 'The role (optional)' : 'Another role'}
                    ariaLabel={`Succeeding into role ${i + 1}`}
                  />
                  {r.title.trim() && (
                    <Select
                      value={r.readiness}
                      onValueChange={(v) =>
                        setDraft((d) => ({
                          ...d,
                          roles: d.roles.map((x, j) => (j === i ? { ...x, readiness: v as Readiness } : x)),
                        }))
                      }
                    >
                      <SelectTrigger className="h-9 w-[11.5rem] text-sm" aria-label={`Readiness for role ${i + 1}`}>
                        <SelectValue placeholder="Readiness" />
                      </SelectTrigger>
                      <SelectContent>
                        {READINESS_ORDER.map((o) => (
                          <SelectItem key={o} value={o}>
                            {READINESS_LABEL[o]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {draft.roles.length > 1 && (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Remove role ${i + 1}`}
                      onClick={() => setDraft((d) => ({ ...d, roles: d.roles.filter((_, j) => j !== i) }))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {draft.roles.length < MAX_ROLES && draft.roles.every((r) => r.title.trim()) && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setDraft((d) => ({ ...d, roles: [...d.roles, { ...emptyRole }] }))}
                  data-demo="talent-campus-add-role"
                >
                  <Plus className="h-3.5 w-3.5" /> Another role
                </Button>
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium">They would also do well somewhere else on the campus</p>
              <input
                list="campus-areas"
                value={draft.crossArea}
                onChange={(e) => setDraft((d) => ({ ...d, crossArea: e.target.value }))}
                placeholder="Which department (optional)"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                aria-label="Which department they would also fit in"
              />
              <datalist id="campus-areas">
                {areas.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
              <Textarea
                value={draft.crossNote}
                onChange={(e) => setDraft((d) => ({ ...d, crossNote: e.target.value }))}
                rows={2}
                className="text-sm"
                placeholder="Why they would fit there"
                aria-label="Why they would fit there"
              />
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">Clearing every field removes your input.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={saving} data-demo="talent-campus-input-save">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
